import { create } from "zustand";
import { isFeatureOn } from "@/lib/early-access";
import {
  type ChecklistItemId,
  type ChecklistState,
  EMPTY_CHECKLIST,
  isChecklistOpen,
  readChecklist,
  requiredDone,
  writeChecklist,
} from "@/lib/guided/checklist";
import { trackOnboarding } from "@/lib/guided/events";
import { useAuthStore } from "@/store/auth.store";

// The guided first visit's checklist for the signed-in account
// (VITE_GUIDED_FIRST_VISIT_ENABLED). Steps are ticked from wherever they
// happen (welcome, practice note, review card, Copy to chart); ticking is a
// no-op when the flag is off or the list was never started.

interface GuidedChecklistState {
  account: string | null;
  state: ChecklistState;
  load: (account: string | null | undefined) => void;
  start: () => void;
  complete: (item: ChecklistItemId) => void;
  dismiss: () => void;
}

const currentAccount = (): string | null =>
  useAuthStore.getState().userData?.public_id ?? null;

export const useGuidedChecklistStore = create<GuidedChecklistState>()(
  (set, get) => {
    const update = (next: ChecklistState): void => {
      const { account } = get();
      writeChecklist(account, next);
      set({ state: next });
    };
    const ensureLoaded = (): void => {
      const account = currentAccount();
      if (account !== get().account) get().load(account);
    };
    return {
      account: null,
      state: EMPTY_CHECKLIST,
      load: (account) => {
        set({
          account: account ?? null,
          state: account ? readChecklist(account) : EMPTY_CHECKLIST,
        });
      },
      start: () => {
        if (!isFeatureOn("guided-first-visit")) return;
        ensureLoaded();
        const { state, account } = get();
        if (!account || state.started) return;
        update({ ...state, started: true });
      },
      complete: (item) => {
        if (!isFeatureOn("guided-first-visit")) return;
        ensureLoaded();
        const { state, account } = get();
        if (!account || !state.started || state.done.includes(item)) return;
        const next = { ...state, done: [...state.done, item] };
        update(next);
        trackOnboarding("checklist_item_done", { item });
        if (requiredDone(next) && !requiredDone(state))
          trackOnboarding("checklist_completed");
      },
      dismiss: () => {
        ensureLoaded();
        const { state } = get();
        update({ ...state, dismissed: true });
        trackOnboarding("checklist_dismissed", {
          done: state.done.length,
        });
      },
    };
  },
);

// Ticks a step from outside React.
export const completeChecklistItem = (item: ChecklistItemId): void => {
  useGuidedChecklistStore.getState().complete(item);
};

// While the list is open, What's new waits (it would cover the first steps).
export const isGuidedChecklistOpen = (): boolean =>
  isFeatureOn("guided-first-visit") &&
  isChecklistOpen(useGuidedChecklistStore.getState().state);
